import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, Input, Modal, Alert, Icon, toast } from '@/components/ui';
import { listClients, createClient, updateClient, deleteClient, type ClientInput } from '@/lib/clients';
import type { Client } from '@/types/client';

const EMPTY: ClientInput = {
  name: '', companyName: '', email: '', phone: '', address: '', taxId: '', notes: '',
};

export function ClientsPage() {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Client | null>(null);
  const [form, setForm] = useState<ClientInput>(EMPTY);
  const [confirmDelete, setConfirmDelete] = useState<Client | null>(null);

  const query = useQuery({
    queryKey: ['clients', { q }],
    queryFn: () => listClients({ q, pageSize: 100 }),
    placeholderData: (prev) => prev,
  });

  const save = useMutation({
    mutationFn: () => editing ? updateClient(editing.id, form) : createClient(form),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['clients'] });
      toast(editing ? 'Client mis à jour.' : 'Client ajouté.', 'success');
      setOpen(false); setEditing(null); setForm(EMPTY);
    },
    onError: (e: unknown) => toast(e instanceof Error ? e.message : 'Échec de l\'enregistrement', 'danger'),
  });

  const del = useMutation({
    mutationFn: (id: string) => deleteClient(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['clients'] });
      toast('Client supprimé.', 'info');
      setConfirmDelete(null);
    },
  });

  function openCreate() { setEditing(null); setForm(EMPTY); setOpen(true); }
  function openEdit(c: Client) {
    setEditing(c);
    setForm({
      name: c.name, companyName: c.companyName, email: c.email,
      phone: c.phone, address: c.address, taxId: c.taxId, notes: c.notes,
    });
    setOpen(true);
  }
  function bind<K extends keyof ClientInput>(key: K) {
    return {
      value: form[key] as string,
      onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
        setForm((f) => ({ ...f, [key]: e.target.value })),
    };
  }

  const items = query.data?.items ?? [];

  return (
    <div className="stack-lg">
      <div className="row-between">
        <h1 style={{ margin: 0, fontSize: 28, letterSpacing: '-0.025em' }}>Clients</h1>
        <Button variant="primary" size="sm" onClick={openCreate} leadingIcon={<Icon name="plus" size={14} />}>
          Ajouter un client
        </Button>
      </div>

      <Card>
        <div className="filter-row" style={{ marginBottom: 12 }}>
          <Input
            placeholder="Rechercher par nom, entreprise ou e-mail…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            leading={<Icon name="search" size={14} />}
          />
        </div>

        {query.isLoading ? (
          <div className="empty-state">Chargement…</div>
        ) : items.length === 0 ? (
          <div className="empty-state">
            <span className="empty-icon" aria-hidden><Icon name="invoices" size={28} /></span>
            <h3>Aucun client pour le moment</h3>
            <p>Ajoutez votre premier client pour lui adresser des factures.</p>
            <Button variant="primary" size="sm" onClick={openCreate} leadingIcon={<Icon name="plus" size={14} />} style={{ marginTop: 12 }}>
              Ajouter un client
            </Button>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="cl-table">
            <thead>
              <tr>
                <th>Entreprise</th>
                <th>Contact</th>
                <th>E-mail</th>
                <th>Téléphone</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id}>
                  <td><strong>{c.companyName || c.name}</strong></td>
                  <td>{c.name || <span className="muted">—</span>}</td>
                  <td>{c.email || <span className="muted">—</span>}</td>
                  <td className="num">{c.phone || <span className="muted">—</span>}</td>
                  <td>
                    <div style={{ display: 'inline-flex', gap: 8 }}>
                      <button type="button" className="lnk lnk-arrow" onClick={() => openEdit(c)}>Modifier</button>
                      <button
                        type="button"
                        className="lnk lnk-arrow"
                        style={{ color: 'var(--hue-crimson)' }}
                        onClick={() => setConfirmDelete(c)}
                      >
                        Supprimer
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </Card>

      <Modal
        open={open}
        size="lg"
        onClose={() => setOpen(false)}
        title={editing ? 'Modifier le client' : 'Nouveau client'}
        lede="Enregistré ici, accessible partout : sélectionnable lors de la création de facture et pour l'envoi par e-mail ou WhatsApp / SMS."
        footer={
          <>
            <Button variant="ghost" onClick={() => setOpen(false)}>Annuler</Button>
            <Button
              variant="primary"
              disabled={save.isPending || (!form.companyName.trim() && !form.name.trim())}
              onClick={() => save.mutate()}
            >
              {save.isPending ? 'Enregistrement…' : editing ? 'Enregistrer les modifications' : 'Ajouter le client'}
            </Button>
          </>
        }
      >
        {save.error instanceof Error && (
          <Alert tone="danger" title="Impossible d'enregistrer">{save.error.message}</Alert>
        )}

        <div className="client-form">
          <section>
            <h4>Identité</h4>
            <p className="muted">Au moins le nom de l'entreprise ou du contact est requis.</p>
            <div className="form-grid">
              <Input label="Nom de l'entreprise" placeholder="Atlas Technologies SARL" autoFocus {...bind('companyName')} />
              <Input label="Nom du contact" placeholder="Karim Bennani" {...bind('name')} />
            </div>
          </section>

          <section>
            <h4>Canaux d'envoi de la facture</h4>
            <p className="muted">Utilisé pour renseigner l'envoi : e-mail via SMTP, téléphone avec lien direct WhatsApp / SMS.</p>
            <div className="form-grid">
              <Input label="E-mail" type="email" placeholder="contact@atlas.ma" {...bind('email')} />
              <Input label="Téléphone" placeholder="+212 6 00 00 00 00" {...bind('phone')} />
            </div>
          </section>

          <section>
            <h4>Détails de facturation <span className="muted" style={{ fontWeight: 400, fontSize: 12 }}>· facultatif</span></h4>
            <div className="form-grid">
              <div style={{ gridColumn: '1 / -1' }}>
                <label className="field-label" htmlFor="client-address">Adresse</label>
                <textarea
                  id="client-address"
                  className="input"
                  rows={2}
                  placeholder={"123 Boulevard Mohammed V\nCasablanca, Maroc"}
                  value={form.address}
                  onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
                  style={{ minHeight: 64, resize: 'vertical', padding: '8px 12px', fontFamily: 'inherit' }}
                />
              </div>
              <Input label="N° TVA / SIRET / ICE" placeholder="TVA / SIRET / ICE…" {...bind('taxId')} />
              <Input label="Notes (internes)" placeholder="Remarques éventuelles sur ce client" {...bind('notes')} />
            </div>
          </section>
        </div>
      </Modal>

      <Modal
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        title="Supprimer ce client ?"
        lede={`${confirmDelete?.companyName || confirmDelete?.name || ''} sera retiré de votre carnet de clients. Les factures existantes conserveront leurs informations de facturation.`}
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmDelete(null)}>Annuler</Button>
            <Button variant="destructive" onClick={() => confirmDelete && del.mutate(confirmDelete.id)} disabled={del.isPending}>
              Supprimer
            </Button>
          </>
        }
      >
        <div />
      </Modal>
    </div>
  );
}
